<template>
    <div>
        <div class="row">
            <div class="col-md-12">
                <div class="card card-default">
                    <div v-if="!isModal" class="card-header">
                        <h3 class="card-title custom-card-title"><fa :icon="['fas', 'car']"></fa> {{ $t("label.vehicle_information")}}</h3>
                    </div>
                    <div class="card-body pb-0">
                        <div class="alert alert-danger" v-if="vehicle_errors.length > 0">
                            <p class="mb-0"><strong>{{ $t('error.required_vehicle_info') }}</strong></p>
                            <ul>
                                <li v-for="(error, index) in vehicle_errors" :key="index">
                                    {{ error }}
                                </li>
                            </ul>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.plate_number') }}</label>
                            <div class="col-md-8">
                                <input name="plate_number" type="text" v-model="vehicle.plate_number" class="form-control" v-validate="'required'" :placeholder="$t('label.plate_number')" :readonly="isView"/>
                                <span v-if="errors.has('plate_number')" class="text-danger">{{ errors.first('plate_number')}}</span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.nationality') }}</label>
                            <div class="col-md-8">
                                <v-select v-validate="'required'"
                                          name="nationality"
                                          v-model="vehicle.nationality"
                                          :options="countryNames"
                                          :readonly="isView">
                                </v-select>
                                <span v-if="errors.has('nationality')" class="text-danger">{{ errors.first('nationality')}}</span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.type') }}</label>
                            <div class="col-md-8">
                                <select name="type" class="form-control" v-model="vehicle.type" v-validate="'required'" :readonly="isView">
                                    <option v-for="(type, index) in types" :value="type.id" :key="index">{{type.name}}</option>
                                </select>
                                <span v-if="errors.has('type')" class="text-danger">{{ errors.first('type')}}</span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.brand') }}</label>
                            <div class="col-md-8">
                                <input name="brand" type="text" class="form-control" v-validate="'required'" :placeholder="$t('label.brand')" id="brand" v-model="vehicle.brand" :readonly="isView"/>
                                <span v-if="errors.has('brand')" class="text-danger">{{ errors.first('brand')}}</span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.steer') }}</label>
                            <div class="col-md-8">
                                <select v-model="vehicle.steer" name="steer" id="steer" v-validate="'required'" class="form-control" :readonly="isView">
                                    <option v-for="(option,index) in options" :key=index :value="option.value">
                                        {{ option.text }}
                                    </option>
                                </select>
                                <span v-if="errors.has('steer')" class="text-danger">{{ errors.first('steer')}}</span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.color') }}</label>
                            <div class="col-md-8">
                                <input name="color" type="text" class="form-control" v-validate="'required'" :placeholder="$t('label.color')" id="color" v-model="vehicle.color" :readonly="isView"/>
                                <span v-if="errors.has('color')" class="text-danger">{{ errors.first('color')}}</span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.frame_number') }}</label>
                            <div class="col-md-8">
                                <input name="frame_number"  type="text" class="form-control frame_number" id="frame-number" v-validate="'required'" v-model="vehicle.frame_number" :readonly="isView"/>
                                <span v-if="errors.has('frame_number')" class="text-danger">{{ errors.first('frame_number')}}</span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{ $t('label.engine_number') }}</label>
                            <div class="col-md-8">
                                <input name="engine_number" type="text" class="form-control engine_number" id="engine-number" v-validate="'required'" :placeholder="$t('label.engine_number')" v-model="vehicle.engine_number" :readonly="isView"/>
                                <span v-if="errors.has('engine_number')" class="text-danger">{{ errors.first('engine_number')}}</span>
                            </div>
                        </div>
                        <div class="form-group row mb-4">
                            <label class="col-md-4 col-form-label required">{{ $t('label.year') }}</label>
                            <div class="col-md-8">
                                <input name="year" type="number" minlength="4" maxlength="4" class="form-control" id="year" v-validate="'required|numeric'" :placeholder="$t('label.year')" v-model="vehicle.year" :readonly="isView"/>
                                <span v-if="errors.has('year')" class="text-danger">{{ errors.first('year')}}</span>
                            </div>
                        </div>
                    </div>

                    <div class="card-header">
                        <h3 class="card-title custom-card-title"><fa :icon="['fas', 'file-alt']"></fa> {{ $t('label.attachment')}}</h3>
                    </div>
                    <div class="card-body">
                        <div class="form-group row">
                            <label class="col-md-4 col-form-label required">{{$t('label.vehicle_identity_photo')}}</label>
                            <div class="col-md-8">
                                <div class="row">
                                    <div class="col-6">
                                        <a href="#" class="imageupload" @click.prevent="!vehicle.identity_image_front ? $refs.identity_image_front.click() : showImage(vehicle.identity_image_front)" :class="{ 'is-invalid': errors.has('identity_image_front') }" :style="backgroundStyle('identity_image_front')">
                                            <template v-if="!vehicle.identity_image_front">
                                                <fa :icon="['fas', 'image']"></fa><br>
                                                {{$t('label.front_side')}}
                                            </template>
                                        </a>
                                        <input type="file" v-validate="{required : !vehicle.identity_image_front, size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="identity_image_front" name="identity_image_front" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
                                        <span v-if="errors.has('identity_image_front')" class="required">{{ errors.first('identity_image_front')}}</span><br/>
                                        <span class="text-success cursor-pointer"
                                              v-if="typeof vehicle.identity_image_front !== 'undefined' && vehicle.identity_image_front !== '' && !isView"
                                              @click="$refs.identity_image_front.click()"
                                        >{{ $t('label.change_image')}}</span>
                                    </div>
                                    <div class="col-6">
                                        <a href="#" class="imageupload" @click.prevent="!vehicle.identity_image_back ? $refs.identity_image_back.click() : showImage(vehicle.identity_image_back)" :class="{ 'is-invalid': errors.has('identity_image_back') }" :style="backgroundStyle('identity_image_back')">
                                            <template v-if="!vehicle.identity_image_back">
                                                <fa :icon="['fas', 'image']"></fa><br>
                                                {{$t('label.back_side')}}
                                            </template>
                                        </a>
                                        <input type="file" v-validate="{required : !vehicle.identity_image_back, size:10240 ,ext : ['jpeg','png','jpg']}" class="d-none" ref="identity_image_back" name="identity_image_back" accept="image/x-png,image/gif,image/jpeg" @change="previewFiles">
                                        <span v-if="errors.has('identity_image_back')" class="required">{{ errors.first('identity_image_back')}}</span><br/>
                                        <span class="text-success cursor-pointer"
                                              v-if="typeof vehicle.identity_image_back !== 'undefined' & vehicle.identity_image_back !== '' && !isView"
                                              @click="$refs.identity_image_back.click()"
                                        >{{ $t('label.change_image')}}</span>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div class="form-group row mt-4">
                            <label class="col-md-4 col-form-label">{{ $t('label.other') }}</label>
                            <div class="col-md-8">
                                <div class="row">
                                    <div class="col-6">
                                        <a class="btn btn-sm btn-secondary" href="#" @click.prevent="!isView ? $refs.other.click() : () => {}">
                                            <fa :icon="['fas', 'paperclip']"></fa>
                                            {{$t('label.choose_document')}}
                                        </a>
                                        <input type="file" v-validate="{size:10240 ,ext : ['jpeg','png','jpg','pdf']}" class="d-none" ref="other" name="other" @change="previewFiles">
                                    </div>
                                    <div class="col-6 mt-2">
                                        <span class="text-success cursor-pointer"
                                              v-if="typeof vehicle.other !== 'undefined' && vehicle.other !== ''"
                                              @click="showImage(vehicle.other)"
                                        >{{ $t('label.view_attachment')}}</span>
                                    </div>
                                    <span class="text-muted ml-2 mt-2">{{ $t('label.other_file_criteria')}}</span>
                                    <span v-if="errors.has('other')" class="ml-2 mt-2 required">{{ errors.first('other')}}</span><br/>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div v-if="isSetupVehicle && !isModal" class="card-footer">
                        <div class="float-right">
                            <button type="button" v-if="!isView" class="btn btn-primary" @click.prevent="store()">{{ $t('label.save') }}</button>
                            <router-link v-else-if="vehicle.uuid" class="btn btn-sm btn-primary" :to="{ name: 'EditVehicle', params: { id: vehicle.uuid }}" :title="$t('label.edit')">
                                <fa :icon="fas.faEdit"/> {{ $t('label.edit') }}
                            </router-link>
                        </div>
                    </div>

                </div>
            </div>
        </div>
        <div v-if="!isSetupVehicle" class="row">
            <div class="col-12">
                <div v-if="isSetupProfile" class="text-left">
                    <button type="button" @click="$emit('back')" class="btn float-left">
                        <fa :icon="fas.faChevronLeft"></fa>
                        {{ $t("label.back")}}
                    </button>
                </div>
                <div v-if="isSetupProfile" class="text-right">
                    <button type="button" class="btn btn-primary float-right" @click.prevent="store()">
                        {{ $t("label.save_continue")}}
                        <fa :icon="fas.faChevronRight"></fa>
                    </button>
                </div>
                <div v-else>
                    <button type="button" class="btn btn-primary text-right" @click.prevent="store()">
                        {{ $t("label.save")}}
                    </button>
                </div>
            </div>
        </div>
        <div v-if="isModal && !isView" class="row">
          <div class="col-12">
            <div class="float-right">
              <button type="button" class="btn btn-secondary mr-2" data-dismiss="modal">
                {{ $t('label.close') }}
              </button>
              <button type="button" class="btn btn-primary" @click.prevent="store()">{{ $t('label.save') }}</button>
            </div>
          </div>
        </div>
    </div>
</template>

<script>
    import { fas } from "@fortawesome/free-solid-svg-icons";
    export default {
        name:'Vehicle',
        props: {
            vehicle_uuid: String,
            isView:{
                type: Boolean,
                default: false
            },
            isSetupProfile: {
                type: Boolean,
                default: false
            },
            isSetupVehicle:{
                type: Boolean,
                default: true
            },
            vehicles: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            isModal:{
              type: Boolean,
              default: false
            },
        },
        data() {
            return {
                baseURI: "",
                imageBaseUrl: process.env.VUE_APP_BACKEND_API_URL + "/vehicles/",
                vehicle: {
                    id: "",
                    plate_number: "",
                    nationality_code: "KH",
                    nationality: "Cambodia",
                    type: "",
                    brand: "",
                    steer: "",
                    color: "",
                    frame_number: "",
                    engine_number: "",
                    year: "",
                    identity_image_front: "",
                    identity_image_back: "",
                    other: "",
                },
                types: [],
                //countries: [],
                vehicle_errors: [],
                options: [
                    { text: 'Left', value: 'Left' },
                    { text: 'Middle', value: 'Middle' },
                    { text: 'Right', value: 'Right' },
                    { text: 'Other', value: 'Other' }
                ]
            }
        },
        watch: {
            vehicle_uuid: function (val, old) {
                if(val || val != ""){
                    this.getVehicle(val);
                }else{
                    this.clearVehicle();
                    this.vehicle.steer = "Left";
                }
            },
            "vehicle.nationality": function(val){
                if(val !== ''){
                    let vehilce_nationality = this.countries.filter(function(country){
                        return country.name === val;
                    });
                    if(vehilce_nationality.length > 0){
                        this.vehicle.nationality_code = vehilce_nationality[0].code;
                    }
                }
            },
        },
        computed: {
            countryNames() {
                return this.countries.map(function(country) {
                    return country.name;
                });
            },
            fas() {
                return fas;
            },
            countries(){
              return this.$store.getters['reference/countries'];
            }
        },
        mounted() {
            //this.getCountries();
            if(this.$route.params.id){
                this.getVehicle();
            }
            else if(this.vehicle_uuid){
                this.getVehicle(this.vehicle_uuid);
            }else if(this.vehicles.length > 0){
                // this.vehicle = this.vehicles[0];
                this.vehicle = Object.assign({}, this.vehicles[0]);
                this.getVehicle(this.vehicles[0].uuid);
            }
            this.getTypes();
            this.initAlphaNumericOnly();
            this.vehicle.steer = "Left";

            $('#engine-number').alphaNumericOnly();
            $('#frame-number').alphaNumericOnly();
            $('#vehicleModal').on('hidden.bs.modal', () => {
                this.vehicle_errors = [];
                this.clearInputFile('#identity_image_front','#span_identity_image_front');
                this.clearInputFile('#identity_image_back','#span_identity_image_back');
                this.clearInputFile('#other','#span_other');
            });
        },
        methods: {
            getCountries(){
                this.$store.state.loading.status = true;
                let url = this.baseURI + "reference/countries";
                GDCE_Client.requestApi("GET",url).then(response => {
                    this.countries = response.data;
                    this.$store.state.loading.status = false;
                }).catch(()=>{});
            },
            getVehicle(uuid = null){
                let url = '';
                if(uuid === null){
                    url = this.baseURI+'/vehicles/'+ this.$route.params.id
                }else{
                    url = this.baseURI + "/vehicles/" + uuid;
                }
                this.$store.state.loading.status = true;
                GDCE_Client.requestApi("GET",url).then(response => {
                    this.vehicle = response.data;
                    let nospace = this.vehicle.steer;
                    if(nospace) {
                        this.vehicle.steer = nospace.trim();
                    }
                    this.vehicle_errors = [];
                    this.$store.state.loading.status = false;
                }).catch(()=>{});
            },
            getTypes(){
                let url = this.baseURI + "reference/types";
                GDCE_Client.requestApi("GET",url).then(response => {
                    this.types = response.data;
                }).catch(()=>{});
            },
            store(){
                this.$validator.validate().then(result => {
                    if (result) {
                        let url = this.baseURI + "/vehicles";
                        let method = "POST";
                        if(this.vehicle.uuid){
                            url = this.baseURI + 'vehicles/' + this.vehicle.uuid;
                            method = "PATCH";
                        }
                        if(this.validFrame() && this.validEngine()){
                            this.$store.state.loading.status = true;
                            GDCE_Client.requestApi(method, url, this.vehicle).then(response => {
                                if(response.status){
                                    if(this.vehicle.uuid){
                                        this.uploadImage(this.vehicle.uuid);
                                    }else{
                                        this.uploadImage(response.data.uuid);
                                    }
                                    if(this.isModal){
                                      this.$store.dispatch('application/setVehicle', response.data)
                                    }
                                }
                            }).catch(error => {
                                this.$store.state.loading.status = false;
                                if (typeof error.data === 'object') {
                                    this.vehicle_errors = _.flatten(_.toArray(error.data.errors));
                                } else {
                                    this.vehicle_errors = ['Something went wrong. Please try again.'];
                                }
                            });
                        }
                    }
                })
            },
            clearVehicle(){
                this.vehicle = {
                    id: "",
                    plate_number: "",
                    nationality_code: "",
                    nationality: "",
                    type_id: "",
                    brand: "",
                    steer: "",
                    color: "",
                    frame_number: "",
                    engine_number: "",
                    year: ""
                }
                this.$validator.reset();
            },
            validFrame(){
                var frame = this.vehicle.frame_number;
                this.vehicle_errors = [];
                if(this.validateVin(frame)){
                    //this.vehicle_errors.push("Frame Number must not contain letter I, O, Q and Space");
                    //this.vehicle_errors.push("Frame Number must not contain character O and Space");
                    this.vehicle_errors.push("Frame Number must not contain Space");
                    return false;
                }
                return true;
            },
            validEngine(){
                var engine = this.vehicle.engine_number;
                this.vehicle_errors = [];
                if(this.validateVin(engine)){
                    //this.vehicle_errors.push("Engine Number must not contain letter I, O, Q and Space");
                    //this.vehicle_errors.push("Engine Number must not contain character O and Space");
                    this.vehicle_errors.push("Engine Number must not contain Space");
                    return false;
                }
                return true;
            },
            validateVin(input){
                var engine_number = input.toUpperCase();
                //let exclude = ["I", "O", "Q", " "];
                //let exclude = ["I", "O", " "];
                //let exclude = ["O", " "];
                let exclude = [" "];
                var status = false;
                exclude.forEach(function(letter) {
                    for(var i = 0;i < engine_number.length; i++){
                        if(engine_number.charAt(i) == letter){
                            status = true;
                        }
                    }
                });
                return status;
            },
            initAlphaNumericOnly(){
                jQuery.fn.alphaNumericOnly = function() {
                    $(this).keydown(function(e) {
                        var key = e.which || e.keyCode;
                        if(e.shiftKey && key >= 48 && key <= 57) {
                            return false;
                        }else {
                            if(key >= 186 && key <= 187 || key >= 191 && key <= 222) {
                                return false;
                            }else {
                                return true;
                            }
                        }
                    });
                }
            },
            uploadImage(uuid) {
                let url = this.baseURI + "/vehicles/" + uuid + "/upload";
                let formData = new FormData();
                formData.append("identity_image_front", this.$refs.identity_image_front.files[0]);
                formData.append("identity_image_back", this.$refs.identity_image_back.files[0]);
                formData.append("other",this.$refs.other.files[0]);
                const config = {
                    headers: { "Content-Type": "multipart/form-data" }
                };
                GDCE_Client.requestApi("POST", url, formData, config).then(response => {
                    this.$store.state.loading.status = false;
                    if (response.status) {
                      this.$swal({
                        position: 'center',
                        icon: 'success',
                        title: this.$t('message.vehicle_success'),
                        showCloseButton: true,
                        timer: 1500
                      });
                      if(this.isModal){
                          return;
                      }
                      if(this.isSetupVehicle){
                          this.$router.push({ name: "VehicleDetail", params:{ id: uuid } });
                      }else{
                          this.$emit('saveAndContinue');
                      }
                    }
                }).catch((error)=>{
                  this.$swal({
                    icon: 'error',
                    title: 'Oops...',
                    text: error,
                  });
                });
            },
            checkImageExist(){
                var identity_front = this.$refs.identity_image_front.files[0];
                var identity_back = this.$refs.identity_image_back.files[0];
                if(identity_front !== undefined){
                    this.vehicle.identity_image_front = true;
                }else{
                    this.vehicle.identity_image_front = "";
                }
                if(identity_back !== undefined){
                    this.vehicle.identity_image_back = true;
                }else{
                    this.vehicle.identity_image_back = "";
                }
            },
            clearInputFile(elId,span){
                $(span).html(this.$i18n.t('label.choose_image'));
                var $el = $(elId);
                $el.wrap('<form>').closest('form').get(0).reset();
                $el.unwrap();
            },
            backgroundStyle(item) {
                if (!this.vehicle[item]) {
                    return '';
                }

                let image = null;
                if(typeof this.vehicle[item] === 'string'){
                    let path = this.vehicle[item].split('/');
                    path =  path[2] + '/' + path[3];
                    image = this.imageBaseUrl + path;
                }else{
                    if (this.vehicle[item].file_path) {
                        image = this.vehicle[item].file_path
                    } else {
                        if(typeof this.vehicle[item] === 'object'){
                            image = URL.createObjectURL(this.vehicle[item])
                        }
                    }
                }
                return `background-image:url('${image}');opacity:1;`
            },
            showImage(src) {
                if(typeof src === 'string'){
                    let path = src.split('/');
                    path =  path[2] + '/' + path[3];
                    window.open( this.imageBaseUrl + path);
                }else{
                    let image = URL.createObjectURL(src)
                    window.open(image);
                }
            },
            previewFiles(event) {
                const file = event.target.files[0]
                if (file.type && file.type.indexOf('image') === -1 && file.type.indexOf('pdf') === -1) {
                    return;
                }
                this.vehicle[event.target.name] = file
            },
        }
    }
</script>

<style lang="scss" scoped>
    input.frame_number, input.engine_number{
        text-transform: uppercase;
    }
    h3.custom-card-title svg{
        font-size: 22px !important;
    }
    .custom-card-title{
        color: #205670;
        text-transform: none !important;
    }

    .imageupload {
        height: 130px;
        position: relative;
        width: 100%;
        background-color: #eeeeee;
        text-align: center;
        border: 1px solid #dddddd;
        -webkit-border-radius: 2px;
        border-radius: 2px;
        opacity: .5;
        font-size: 14px;
        display: block;
        text-decoration: none;
        color: #555555;
        min-height: 100px;
        padding-top: 35px;
        background-repeat: no-repeat;
        background-position: top center;
        background-size: 100% auto;

        svg {
            font-size: 26px;
            color: #666666;
        }

        &:hover,
        &.selected {
            opacity: 1;
        }

        &.is-invalid {
            opacity: 1;
            border-color: #dc3545;
            background-color: rgba($color: #dc3545, $alpha: .05);
            color: #dc3545;
        }
    }
    label.required {
        &::after {
            content: " *";
            color: red;
        }
    }
    span.required {
        color: red;
        font-size: 12px;
    }
</style>
